/**
 * The rules the platform actually runs on, in plain English.
 * Source of truth: /app/memory/PAYMENT_SPEC.md. Update both together.
 */
import { Card, CardContent, CardHeader } from '@/components/ui/card';

const Row = ({ label, children }) => (
  <div className="grid sm:grid-cols-[180px_1fr] gap-1 sm:gap-4 py-2 border-b border-white/10 last:border-0">
    <span className="text-cyan-300 font-medium">{label}</span>
    <span className="text-gray-200">{children}</span>
  </div>
);

export const PricingNotesCard = ({ table }) => {
  const tiers = table?.tiers || {};
  const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);

  return (
    <Card className="bg-slate-900/60 border-cyan-500/30" data-testid="pricing-notes-card">
      <CardHeader>
        <h2 className="text-2xl font-bold text-white">Pricing Notes</h2>
        <p className="text-sm text-gray-400">
          How every membership dollar is calculated. These notes match the payment engine —
          if a figure here looks wrong, the engine is what needs changing.
        </p>
      </CardHeader>
      <CardContent className="text-sm">
        <Row label="Three fees">
          <strong>
            {table?.processing_pct ?? 3}% + ${Number(table?.processing_fixed ?? 0.3).toFixed(2)} processing
          </strong>{' '}
          on every payment ·{' '}
          <strong>{table?.convenience_pct ?? 10}% financing</strong> on payment-plan instalments only ·{' '}
          <strong>sales tax</strong> on top. None of the three is ever credited or refunded.
        </Row>
        <Row label="Two ways to buy">
          Pay in full (no financing fee), or the tier's payment plan. Payment one is taken at
          checkout, then the same day of each following month.
        </Row>
        <Row label="Payment plans">
          {['silver', 'gold', 'platinum'].map((t) => (
            <span key={t} className="block">
              <span className="capitalize">{t}</span>: {tiers[t]?.plan_count ?? '—'} payments ·{' '}
              {money(tiers[t]?.payments?.[0]?.billed)} each
              {' '}(plan total {money(tiers[t]?.plan_total)}) · pay in full {money(tiers[t]?.pay_in_full)}
            </span>
          ))}
        </Row>
        <Row label="Tiers are inclusive">
          Gold already contains Silver. Nobody pays for both, and downgrades are not offered.
        </Row>
        <Row label="Upgrade credit">
          The base dollars paid on the tier they are on <em>now</em>, capped at that tier's base
          price. Not cumulative. Paying in full counts as finishing the whole plan. Add-ons never
          credit. Because every cap sits below the next tier's price, a free upgrade is impossible.
        </Row>
        <Row label="Maintenance fee">
          Charged annually, first on the 12-month anniversary of becoming a paid member. That
          anniversary <strong>never moves</strong> — not on upgrade, not on reinstatement.
        </Row>
        <Row label="Missed payment">
          Two retries, one a day. If we still can't collect, the member drops to Bronze and their
          plan is preserved untouched. Paying restores their exact spot — no recalculation, no
          penalty, no deadline.
        </Row>
        <Row label="Cancellation">
          Access ends immediately and remaining payments are never owed. Full refund of the
          <strong> base only</strong> within 7 days of the <strong>first</strong> payment — never the
          fees or tax. Credit is forfeited; coming back is full price with a fresh anniversary.
          No waiting period.
        </Row>
        <Row label="Drip removal">
          One global price, one-time, permanent, applies across the whole coaching program and
          survives every upgrade. Never credited, never refunded.
        </Row>
      </CardContent>
    </Card>
  );
};

export default PricingNotesCard;
